// Premium Propulse Spin Wheel — casino-style glossy 3D wheel with gold pointer,
// shimmer, confetti on big prizes, sound cues, jackpot announcements, and a learning tip
// shown after each spin to keep the "learning by playing" promise alive.
import React, { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Volume2, VolumeX, RefreshCw, Award, Lightbulb } from 'lucide-react';
import { GameFrame, AnnouncementBanner, ScoreTile, playGameSound, GoldButton } from './shared/GameFX';
import { ConfettiBurst, Fireworks } from './ludo/LudoConfetti';

const LEARN_TIPS = [
    { title: 'FR vs FRLS', body: 'FRLS adds low smoke generation to flame retardance — essential for crowded indoor spaces.' },
    { title: 'IS 694', body: 'IS 694 governs PVC insulated single-core house wires up to 1100V.' },
    { title: 'XLPE cables', body: 'XLPE handles 90°C continuous operation vs PVC at 70°C — better for HVAC and heavy load circuits.' },
    { title: 'LOTO 6-step', body: 'Notify → Identify → Isolate → Lockout/Tagout → Verify zero energy → Test before touch.' },
    { title: 'Drum storage', body: 'Store cable drums upright on flanges in covered, dry space. Lying flat crushes inner layers.' },
    { title: 'Counterfeit alert', body: 'Test conductor with a magnet — pure copper is non-magnetic; CCA gives a faint pull.' },
];

export default function SpinWheel({ game, onPlayed }) {
    const segs = game.config?.segments || [];
    const [angle, setAngle] = useState(0);
    const [spinning, setSpinning] = useState(false);
    const [result, setResult] = useState(null);
    const [muted, setMuted] = useState(false);
    const [announce, setAnnounce] = useState(null);
    const [bursts, setBursts] = useState([]);
    const [showFireworks, setShowFireworks] = useState(false);
    const [spinCount, setSpinCount] = useState(0);
    const [totalPts, setTotalPts] = useState(0);
    const [tip, setTip] = useState(null);
    const burstIdRef = useRef(0);
    const seg = 360 / Math.max(1, segs.length);

    const triggerBurst = () => {
        burstIdRef.current += 1;
        const id = burstIdRef.current;
        setBursts((b) => [...b, { id, count: 40, scale: 1.2 }]);
        setTimeout(() => setBursts((b) => b.filter((x) => x.id !== id)), 1600);
    };

    const announce_ = (text, tone = 'gold') => {
        const id = Date.now() + Math.random();
        setAnnounce({ text, tone, id });
        setTimeout(() => setAnnounce((a) => (a && a.id === id ? null : a)), 1500);
    };

    const spin = async () => {
        if (spinning || segs.length === 0) return;
        setSpinning(true); setResult(null); setTip(null);
        playGameSound('roll', muted);
        const idx = Math.floor(Math.random() * segs.length);
        const finalAngle = 360 * 6 + (360 - (idx * seg + seg / 2));
        setAngle(angle + finalAngle);
        setTimeout(async () => {
            setSpinning(false);
            const prize = segs[idx];
            setResult(prize);
            setSpinCount((c) => c + 1);
            setTotalPts((p) => p + (prize.value || 0));
            // Effects scaled to prize size
            const isJackpot = (prize.value || 0) >= 50;
            if (isJackpot) {
                playGameSound('jackpot', muted);
                setShowFireworks(true);
                setTimeout(() => setShowFireworks(false), 3500);
                triggerBurst();
                announce_(`🏆 ${prize.label}!`, 'gold');
            } else {
                playGameSound('correct', muted);
                announce_(`+${prize.value} pts`, 'green');
            }
            // Pick a random learning tip
            setTip(LEARN_TIPS[Math.floor(Math.random() * LEARN_TIPS.length)]);
            try {
                await api.post(`/games/${game.code}/submit`, {
                    score: prize.value,
                    awarded_points: prize.value,
                    details: { segment: prize.label },
                });
                onPlayed && onPlayed();
            } catch (e) { console.warn('Spin submit failed', e); }
        }, 4200);
    };

    return (
        <GameFrame dataTestId="spin-wheel-game" orbs={[
            { color: '#FFF200', x: '12%', y: '20%', size: 230, delay: 0 },
            { color: '#E31E24', x: '85%', y: '25%', size: 210, delay: 1.4 },
            { color: '#f59e0b', x: '50%', y: '85%', size: 240, delay: 2.8 },
        ]}>
            <Header muted={muted} onToggleMute={() => setMuted((m) => !m)} spinCount={spinCount} totalPts={totalPts} />
            <div className="mt-4 flex flex-col items-center">
                <Wheel angle={angle} segs={segs} seg={seg} />
                <div className="mt-6 flex flex-col sm:flex-row items-center gap-4 w-full justify-center">
                    <GoldButton onClick={spin} disabled={spinning} dataTestId="spin-btn">
                        <Sparkles className="h-4 w-4 sm:h-5 sm:w-5" />
                        {spinning ? 'Spinning…' : 'SPIN'}
                    </GoldButton>
                    {result && !spinning && (
                        <motion.div
                            initial={{ scale: 0.6, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }}
                            className="px-5 py-3 rounded-2xl bg-white/95 text-slate-900 font-display font-extrabold inline-flex items-center gap-2 shadow-lg" data-testid="spin-result">
                            <Award className="h-5 w-5 text-amber-500" /> Won: <span className="text-emerald-700">{result.label}</span>
                        </motion.div>
                    )}
                </div>
                {/* Learning tip card */}
                <AnimatePresence>
                    {tip && (
                        <motion.div
                            key={tip.title}
                            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -10, opacity: 0 }}
                            className="mt-6 w-full max-w-md p-4 rounded-2xl bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200 shadow-lg"
                            data-testid="spin-tip"
                        >
                            <div className="flex items-center gap-2 text-amber-700 text-[10px] uppercase tracking-wider font-bold">
                                <Lightbulb className="h-3 w-3" /> Learning Tip
                            </div>
                            <div className="font-display font-extrabold text-slate-900 mt-1">{tip.title}</div>
                            <div className="text-sm text-slate-700 mt-1 leading-relaxed">{tip.body}</div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
            {/* Confetti and fireworks */}
            {bursts.map((b) => <ConfettiBurst key={b.id} count={b.count} scale={b.scale} />)}
            {showFireworks && <Fireworks duration={3.5} density={1.2} />}
            <AnnouncementBanner announce={announce} />
        </GameFrame>
    );
}

function Header({ muted, onToggleMute, spinCount, totalPts }) {
    return (
        <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center"
                style={{ boxShadow: '0 0 20px rgba(245,158,11,0.5)' }}>
                <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-amber-300">Propulse Cables & Wires</div>
                <div className="font-display font-extrabold text-lg sm:text-2xl text-white truncate">Spin the Wheel</div>
                <div className="text-[11px] sm:text-xs text-slate-400">Land on a slice, win the prize — every spin teaches.</div>
            </div>
            <div className="flex gap-2">
                <ScoreTile label="Spins" value={spinCount} accent="from-sky-400 to-blue-500" />
                <ScoreTile label="Pts" value={totalPts} accent="from-amber-400 to-yellow-500" />
            </div>
            <button onClick={onToggleMute} className="w-9 h-9 rounded-xl bg-white/10 text-white flex items-center justify-center hover:bg-white/20" data-testid="spin-mute-btn">
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </button>
        </div>
    );
}

function Wheel({ angle, segs, seg }) {
    return (
        <div className="relative">
            {/* Outer rim glow */}
            <div className="absolute -inset-3 rounded-full pointer-events-none"
                style={{ background: 'radial-gradient(circle at center, rgba(255,242,0,0.25) 0%, transparent 70%)', filter: 'blur(20px)' }} />
            {/* Pointer (top, gold) */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
                <div className="w-0 h-0"
                    style={{ borderLeft: '14px solid transparent', borderRight: '14px solid transparent', borderTop: '22px solid #FFD700', filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.6))' }} />
            </div>
            {/* Outer ring with bezel */}
            <div className="relative rounded-full p-2"
                style={{
                    background: 'conic-gradient(from 0deg, #FCD34D, #F59E0B, #FCD34D, #B45309, #FCD34D, #F59E0B, #FCD34D)',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.6), inset 0 4px 6px rgba(255,255,255,0.4), inset 0 -4px 6px rgba(0,0,0,0.5)',
                }}>
                <motion.svg
                    viewBox="0 0 200 200" className="w-72 h-72 sm:w-80 sm:h-80 rounded-full block"
                    animate={{ rotate: angle }} transition={{ duration: 4, ease: [0.17, 0.67, 0.83, 0.99] }}
                    style={{ filter: 'drop-shadow(0 0 25px rgba(0,0,0,0.5))' }}
                >
                    <defs>
                        <radialGradient id="wheelShine" cx="50%" cy="40%" r="60%">
                            <stop offset="0%" stopColor="white" stopOpacity="0.35" />
                            <stop offset="60%" stopColor="white" stopOpacity="0" />
                        </radialGradient>
                        {segs.map((s, i) => (
                            <linearGradient key={i} id={`seg-${i}`} x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0%" stopColor={lighten(s.color, 0.25)} />
                                <stop offset="100%" stopColor={s.color} />
                            </linearGradient>
                        ))}
                    </defs>
                    {segs.map((s, i) => {
                        const start = i * seg;
                        const end = start + seg;
                        const r = 96;
                        const x1 = 100 + r * Math.cos((Math.PI / 180) * (start - 90));
                        const y1 = 100 + r * Math.sin((Math.PI / 180) * (start - 90));
                        const x2 = 100 + r * Math.cos((Math.PI / 180) * (end - 90));
                        const y2 = 100 + r * Math.sin((Math.PI / 180) * (end - 90));
                        const largeArc = seg > 180 ? 1 : 0;
                        const labelAngle = (start + seg / 2 - 90) * (Math.PI / 180);
                        const lx = 100 + 60 * Math.cos(labelAngle);
                        const ly = 100 + 60 * Math.sin(labelAngle);
                        return (
                            <g key={i}>
                                <path d={`M 100 100 L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z`} fill={`url(#seg-${i})`} stroke="#FFFFFF" strokeWidth="1.5" />
                                <text x={lx} y={ly} fill="white" fontSize="9.5" fontWeight="800" textAnchor="middle" alignmentBaseline="middle"
                                    style={{ filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.6))' }}
                                    transform={`rotate(${start + seg / 2} ${lx} ${ly})`}>
                                    {s.label}
                                </text>
                                {/* Pin marker on edge */}
                                <circle cx={100 + 92 * Math.cos((Math.PI / 180) * (start - 90))}
                                    cy={100 + 92 * Math.sin((Math.PI / 180) * (start - 90))}
                                    r="2" fill="#FFD700" stroke="#7B5800" strokeWidth="0.4" />
                            </g>
                        );
                    })}
                    {/* Shine overlay */}
                    <circle cx="100" cy="100" r="96" fill="url(#wheelShine)" />
                    {/* Inner hub */}
                    <circle cx="100" cy="100" r="22" fill="#231F20" stroke="#FFD700" strokeWidth="3" />
                    <circle cx="100" cy="100" r="14" fill="url(#wheelShine)" stroke="#FFD700" strokeWidth="1" />
                    <text x="100" y="103" fill="#FFD700" fontSize="9" fontWeight="900" textAnchor="middle">UT</text>
                </motion.svg>
            </div>
        </div>
    );
}

function lighten(hex, amt) {
    if (!hex || !hex.startsWith('#')) return hex;
    let h = hex.replace('#', '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    const r = Math.min(255, parseInt(h.slice(0, 2), 16) + Math.round(amt * 100));
    const g = Math.min(255, parseInt(h.slice(2, 4), 16) + Math.round(amt * 100));
    const b = Math.min(255, parseInt(h.slice(4, 6), 16) + Math.round(amt * 100));
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}
